import React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  MuiThemeProvider,
  Tooltip,
  Typography,
  TypographyProps,
  withStyles,
  IconButton,
  OutlinedInput,
} from '@material-ui/core';
import { orderBy, cloneDeep, clone, without, isNil, isEmpty, groupBy, isEqual } from 'lodash';
import TemplateModalWithButton from 'src/components/TemplateModalWithButton/TemplateModalWithButton';
import viewConfiguratorStyle, {
  getItemStyle,
  getListStyle,
  configuratorAccordionOverrides,
  configuratorAccordionSummaryOverrides,
  configuratorAccordionDetailsOverrides,
  configuratorAccordionTypographyOverrides,
  resetButtonStyles,
} from './ViewConfiguratorModal.styles';
import { TenantConfigViewData, TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { Option } from '../Configure/ConfigureModal';
import { FavoriteListItemStorage } from 'src/components/Subheader/Favorites/Favorites.types';
import { ColumnApi } from '@ag-grid-community/core';
import SubheaderQuickActionButton from '../Subheader/SubheaderQuickActionButton';
import { muiTheme } from 'src/utils/Style/Theme';
import { ExpandMore, Close } from '@material-ui/icons';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import ServiceContainer from 'src/ServiceContainer';
import { flattenConfigItems } from '../ConfigurableGrid/ConfigurableGrid';
import { classes } from 'typestyle';

export interface CompanionFavoriteData {
  companionSortDirection: SortByDirection;
  companionSortField?: string;
}

export interface ViewConfiguratorModalProps {
  viewConfig: TenantConfigViewData;
  getColumnApi?: () => ColumnApi | undefined; // Get an up to date grid data
  unmodifiedViewDefn: TenantConfigViewData;
  companionData?: CompanionFavoriteData;
  defaultCompanionData?: CompanionFavoriteData;
  configureSelections?: Option[];
  defaultConfigureSelections?: Option[];
  showPinCheckboxForGrid?: boolean;
  subheaderTitle?: string;
  updateConfig?: (config: TenantConfigViewData) => void;
  updateConfiguration?: (configurationSelections: Option[]) => void;
  onSubmit?: (config: TenantConfigViewData) => void;
}

export interface ViewConfiguratorModalState {
  showResetConfirmation: boolean;
  viewConfig: TenantConfigViewItem[];
  onOpenViewConfig: TenantConfigViewItem[];
  openOverride?: boolean;
  searchValue: string;
}

export interface LocalStorageConfig extends FavoriteListItemStorage { }

export interface StyledTypographyProps extends TypographyProps {
  hasIcon: boolean;
}

const StyledAccordion = withStyles(() => configuratorAccordionOverrides)(Accordion);
const StyledAccordionSummary = withStyles(() => configuratorAccordionSummaryOverrides)(AccordionSummary);
const StyledAccordionDetails = withStyles(() => configuratorAccordionDetailsOverrides)(AccordionDetails);
const StyledTypography = withStyles(() => configuratorAccordionTypographyOverrides)(Typography);

/** Function to apply grouping of views with groupingKey (ConfigurableGrid) */
const applyGroupingInPlace = (items: TenantConfigViewItem[]): TenantConfigViewItem[] => {
  // if we already have nested columns, *don’t rebuild* groups—
  // but *do* fill in any undefined dataIndex on the headers because it messes up selection otherwise
  if (items.some((item) => Array.isArray(item.columns))) {
    return items.map((item, idx) => {
      if (Array.isArray(item.columns)) {
        return {
          ...item,
          // if dataIndex is falsy, give it a unique one
          dataIndex: item.dataIndex ?? `group-${item.groupingKey ?? 'header'}-${idx}`,
        };
      }
      return item;
    });
  }

  // build groupedColumns in one pass, grouping only items with same key that are adjacent
  const groupedColumns = items.reduce<TenantConfigViewItem[]>((groupedCols, current, idx, arr) => {
    const key = current.groupingKey || '';
    const prev = groupedCols[groupedCols.length - 1];

    // if we've already started a group for this key, add it in
    if (prev?.columns && prev.groupingKey === key) {
      prev.columns.push(current);

      // otherwise, if the next item shares this key, start a new group header
    } else if (key && arr[idx + 1]?.groupingKey === key) {
      groupedCols.push({
        ...current, // base header on this first item for now
        dataIndex: `group-${key}-${idx}`,
        text: key, // show the groupingKey as header text & configurator label
        viewConfiguratorName: key,
        columns: [current], // start the contiguous run here
      });

      // else, emit as a standalone column
    } else {
      groupedCols.push(current);
    }

    return groupedCols;
  }, []);

  // then fix each group-header’s visible/hidden based on its children
  return groupedColumns.map((col) => {
    if (col.columns) {
      // if any child is visible, the header should be visible
      const anyVisible = col.columns.some((c) => c.visible !== false && c.hidden !== true);
      return {
        ...col,
        visible: anyVisible,
        hidden: !!anyVisible,
      };
    }
    return col;
  });
};
//this function to check if the keyGroupedColumns are not matching with current column grid
const hasColumnOrderChanged = (columnApi: ColumnApi, groupedConfig: TenantConfigViewItem[]): boolean => {
  const currentColumnState = columnApi.getColumnState();

  const flatConfig = flattenConfigItems(groupedConfig)
    .filter((col) => col.visible !== false && col.hideFromConfigurator !== true)
    .map((col) => col.id || col.dataIndex)
    .filter(Boolean);

  const visibleGridColIds = currentColumnState
    .filter((col) => !col.hide && flatConfig.includes(col.colId))
    .map((col) => col.colId);

  const filteredConfig = flatConfig.filter((id) => visibleGridColIds.includes(id));

  return !isEqual(filteredConfig, visibleGridColIds);
};

export default class ViewConfiguratorModal extends React.Component<
  ViewConfiguratorModalProps,
  ViewConfiguratorModalState
> {
  constructor(props: ViewConfiguratorModalProps) {
    super(props);
    this.state = {
      viewConfig: [],
      onOpenViewConfig: [],
      showResetConfirmation: false,
      searchValue: '',
    };
  }

  onOpen = async (): Promise<void> => {
    const columnApi = this.props.getColumnApi?.();

    // this need to check the keyGroupedColumns & current column
    const useGroupedSnapshot =
      columnApi &&
      // !this.props.viewConfig.isDefault &&
      this.props.viewConfig.keyGroupedColumns &&
      !hasColumnOrderChanged(columnApi, this.props.viewConfig.keyGroupedColumns);
    const raw =
      // if we’ve already stored a grouped snapshot & equal to current grid column state then use it
      useGroupedSnapshot
        ? this.props.viewConfig.keyGroupedColumns
        : // else if it’s using columns, fallback to other logic
        Array.isArray(this.props.viewConfig.columns)
          ? this.props.viewConfig.columns
          : this.props.viewConfig.view;
    const viewColumnsConfig: TenantConfigViewItem[] = cloneDeep(raw || []);

    // if we've already stored a grouped/nested snapshot, just restore it & skip reformatting
    if (this.props.viewConfig.keyGroupedColumns && useGroupedSnapshot) {
      this.setState({ viewConfig: viewColumnsConfig });
      return;
    }
    // Filter out columns that are fully hidden
    const visibleColumns = viewColumnsConfig.filter(
      (col) => col?.visible !== false && col?.hidden !== true && col?.hideFromConfigurator !== true,
    );
    // Group by dataIndex
    const groupedByDataIndex = groupBy(visibleColumns, 'dataIndex');

    // Find duplicates
    const duplicates = Object.entries(groupedByDataIndex).filter(([_, items]) => items.length > 1);
    // Log an error if duplicates exist
    if (duplicates.length > 0) {
      ServiceContainer.loggingService.warn(
        `Duplicate columns detected in view config by dataIndex: ${duplicates.map(([key]) => key).join(', ')}`,
      );
    }
    // Reorder the config based off the grid, so if the user makes changes via aggrid, they reflect here
    const reorderConfig: TenantConfigViewItem[] = [];
    if (this.props.getColumnApi) {
      const columnApi = this.props.getColumnApi();
      if (columnApi) {
        // If a config has nested columns, check if some were hidden in grid and keep them hidden/unhidden
        const agGridColumnsState = columnApi.getColumnState();
        viewColumnsConfig
          .filter((viewColumnConfig) => !viewColumnConfig.hideFromConfigurator)
          .map((viewColumnConfig, i) => {
            // Determines if the current viewColumnConfig is present in grid columns state
            // in case the user made column changes within the grid before opening modal.
            // Those changes need to be reflected in the configurator modal.
            // Multiple items may be returned if there are columns with the same id/dataIndex value.
            // In the event multiple columns have identiy `dataIndex`s, ag-grid will append `_1`, `_2` etc.
            // to the duplicates.  Because of that, we use `startsWith()` here to check the beginning of the grid state `colId`.
            // What could go wrong?
            const agGridColumnMatches = agGridColumnsState.filter(
              (gridColumn) => {
                return gridColumn.colId.startsWith(viewColumnConfig.id || '🙅') || gridColumn.colId.startsWith(viewColumnConfig.dataIndex)
              },
            );

            // Finds current viewColumnConfig in the viewColumnsConfig.
            // Multiple items may be returned if there are columns with the same id/dataIndex value.
            const viewColumnMatches = viewColumnsConfig.filter(
              (viewColumn) => viewColumn.dataIndex === viewColumnConfig.dataIndex,
            );

            // Some view configs have a group property that shares the same dataIndex as another column in the config.
            // Finds correct config item in viewColumnConfigs that is not supposed to be hidden from the configurator.
            // This is most likely going to filter out the 'Group' column.
            const visibleViewColumnIndex =
              viewColumnMatches && viewColumnMatches.findIndex((a) => !a.hideFromConfigurator);

            // We found the correct agGrid column in state to potentially modify
            const agGridColumn = agGridColumnMatches[visibleViewColumnIndex || 0];

            // Reorder columns based on the grid
            // If the viewConfigItem is found in the gridColumn's state, place it in the order based on grid
            if (agGridColumn) {
              reorderConfig[agGridColumnsState.indexOf(agGridColumn)] = viewColumnConfig;
            }

            // If config item has columns has to be ordered differently
            const cols = viewColumnConfig.columns;
            if (!agGridColumn && cols && cols.length > 0) {
              //check if all child column is not visible
              const nestedColumns = cols.filter((col) => col.visible !== false);
              const gridColumn =
                !isEmpty(nestedColumns) &&
                agGridColumnsState.find(
                  (a) => a.colId.replace(/_\d$/, '') == (nestedColumns[0].id || nestedColumns[0].dataIndex),
                );
              if (gridColumn) {
                reorderConfig[agGridColumnsState.indexOf(gridColumn)] = viewColumnConfig;
              } else {
                // Or else just move it to the end
                reorderConfig[i + 99] = viewColumnConfig;
              }
            }

            // Determine and set column state for configurator,
            // visible determines the checkbox checked state.
            // Checking for hidden and visible in case config is setup with either or (we weren't consistent in config)

            if (agGridColumn && agGridColumn.hide) {
              viewColumnConfig.visible = false;
              viewColumnConfig.hidden = true;
            } else if (agGridColumn && !agGridColumn.hide && !viewColumnConfig.visible) {
              viewColumnConfig.visible = true;
            }

            // Now that we get the actual state of the column per config property,
            // we can change things like pinned as well
            if (agGridColumn && !agGridColumn.pinned) {
              viewColumnConfig.pinned = undefined;
            } else if (agGridColumn && agGridColumn.pinned) {
              viewColumnConfig.pinned = 'left';
            }

            return viewColumnConfig;
          });

        // helpers — use real visibility, not hideFromConfigurator
        const keyOf = (c: TenantConfigViewItem) => c.id ?? c.dataIndex ?? '';
        const sameKey = (a: TenantConfigViewItem, b: TenantConfigViewItem) => keyOf(a) && keyOf(a) === keyOf(b);
        const isCfgVisible = (c: TenantConfigViewItem) =>
          c.visible !== false && c.hidden !== true;

        // match AG Grid duplicate colIds (with the _1, _2 suffixes) to config keys
        const matchesColId = (stateColId: string, key: string) =>
          stateColId === key || stateColId.startsWith(key + '_');

        // Insert skipped columns (e.g., with hideFromConfigurator) into reorderConfig
        // at their correct index from the current grid state instead of pushing them to the end.
        viewColumnsConfig.forEach((columnConfig) => {
          const key = keyOf(columnConfig);
          if (!key) return;

          // If a grid-visible twin with the same key is already placed, skip grid-hidden duplicates
          if (!isCfgVisible(columnConfig) && reorderConfig.some(c => c && sameKey(c, columnConfig) && isCfgVisible(c))) {
            return;
          }

          // Pick the best matching AG slot for this key (prefer pinned > visible > first)
          const candidateIdxs = agGridColumnsState
            .map((s, i) => ({ s, i }))
            .filter(({ s }) => matchesColId(s.colId, key))
            .map(({ i }) => i);

          const gridIndex =
            candidateIdxs.find(i => agGridColumnsState[i].pinned) ??
            candidateIdxs.find(i => !agGridColumnsState[i].hide) ??
            (candidateIdxs.length ? candidateIdxs[0] : -1);

          if (gridIndex !== -1) {
            const existing = reorderConfig[gridIndex];

            // Dont overwrite a grid-visible twin with a grid-hidden one
            if (existing && isCfgVisible(existing) && !isCfgVisible(columnConfig) && sameKey(existing, columnConfig)) {
              return;
            }

            reorderConfig[gridIndex] = columnConfig;
          } else {
            // No grid slot: append only if not a hidden dup of a visible twin
            if (!isCfgVisible(columnConfig) && reorderConfig.some(c => c && sameKey(c, columnConfig) && isCfgVisible(c))) {
              return;
            }
            reorderConfig.push(columnConfig);
          }

        });
      }
    }

    const baseConfig = reorderConfig.length > 0 ? reorderConfig.filter((x) => x) : this.reorderItems(viewColumnsConfig);

    // apply the manual grouping
    const groupedConfig = applyGroupingInPlace(baseConfig);

    this.setState({
      viewConfig: groupedConfig,
    });
  };

  onClose = async () => {
    this.setState({
      openOverride: undefined,
    });
  };

  onCancel = () => {
    this.onClose();
  };

  onSubmit = async () => {
    // Default any column lacking `visible` to true, unless `hideFromConfigurator` is set or missing property visible in option
    const grouped = this.state.viewConfig.map((col) =>
      col.hideFromConfigurator || 'visible' in col || 'hidden' in col ? col : { ...col, visible: true },
    );

    const flat = flattenConfigItems(grouped);

    // start with basic new config, not overrwriting view & columns every time
    const newConfig: TenantConfigViewData = {
      ...this.props.viewConfig,
      isDefault: false,
    };

    // if this view originally had a columns array at the root, update that & store grouped format separately
    if (Array.isArray(this.props.viewConfig.columns)) {
      newConfig.columns = flat;
      newConfig.keyGroupedColumns = grouped;
    }
    // view with view array
    if (Array.isArray(this.props.viewConfig.view)) {
      // preserve exact nested structure
      newConfig.view = grouped;
    }

    // if there’s a grid.view, keep it in sync as before
    if (this.props.viewConfig.grid) {
      newConfig.grid = {
        ...this.props.viewConfig.grid,
        view: grouped,
      };
    }

    if (this.props.updateConfig) {
      this.props.updateConfig(newConfig);
    }
    if (this.props.onSubmit) {
      this.props.onSubmit(newConfig);
    }

    this.onClose();
  };

  onClickPinned = (item: TenantConfigViewItem) => {
    const index = this.state.viewConfig.findIndex(c => c === item);
    if (!this.isPinned(item.pinned as string)) {
      item.pinned = 'left';
      if (item.columns) {
        item.columns = item.columns.map((x) => {
          return { ...x, pinned: 'left' };
        });
      }
    } else {
      item.pinned = undefined;
      if (item.columns) {
        item.columns = item.columns.map((x) => {
          return { ...x, pinned: undefined };
        });
      }
    }
    let updatedItems = clone(this.state.viewConfig); //.slice(0);
    updatedItems[index] = item;
    // updatedItems = this.reorderItems(updatedItems);
    // If it is newly pinned, add it to the front, if we removed pinning, move it to the first non pinned item
    // We don't need to reorder 1 or  fewer
    if (updatedItems.length > 2) {
      if (this.isPinned(item.pinned)) {
        updatedItems = without(updatedItems, item);
        updatedItems.unshift(item);
      } else {
        const firstItemNotPinnedIndex = updatedItems.findIndex((x) => item != x && !x.pinned) - 1;
        updatedItems = without(updatedItems, item);
        updatedItems.splice(firstItemNotPinnedIndex, 0, item);
      }
    }
    this.setState({
      viewConfig: updatedItems,
    });
  };

  isPinned = (pinned: string | undefined) => {
    return pinned === 'left' ? true : false;
  };

  onClickVisible = (item: TenantConfigViewItem) => {
    const itemIndex = this.state.viewConfig.findIndex(c => c === item);
    item.visible = !this.itemVisible(item);
    // add a guard check because some config has hidden/visible at the same time. Both hidden/visible should opposite
    if ('hidden' in item) {
      item.hidden = !item.visible
    }
    // if this is a grouped item, push that same flag to all its children so they hide correctly
    // this is needed for the groupingKey columns, as they aren't nested
    if (item.columns) {
      item.columns.forEach((child) => {
        child.visible = item.visible;
        if ('hidden' in item) {
          child.hidden = !item.visible
        }
      });
    }
    const updatedItems = this.state.viewConfig.slice(0);
    updatedItems[itemIndex] = item;
    if (!item.visible) {
      updatedItems.push(updatedItems.splice(itemIndex, 1)[0]);
    }
    this.setState({
      viewConfig: updatedItems,
    });
  };

  onChildrenClickVisible = (item: TenantConfigViewItem, childrenIndex: number, parentIndex: number) => {
    item.visible = !this.itemVisible(item);
    const parent = this.getFilteredViewConfig()[parentIndex];

    const updatedViewConfig = this.state.viewConfig.slice(0);
    if (parent.columns) {
      parent.columns[childrenIndex] = item;
      const isAllChildrenVisible = parent.columns.every((child) => child.visible === false);
      parent.visible = isAllChildrenVisible ? false : true;
    }

    this.setState({
      viewConfig: updatedViewConfig,
    });
  };

  reorderItems = (items: TenantConfigViewItem[]) => {
    return orderBy(
      items,
      [
        (i) => {
          if (i.visible === false || i.hidden === true) {
            return 1;
          }
          return 0;
        },
        (i) => (i.pinned ? 0 : 1),
      ],
      ["asc", "asc"]
    );
  };

  onDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }

    // CHILD reordering
    if (type.startsWith('CHILD-')) {
      // extract the group's dataIndex
      const groupDataIndex = type.replace('CHILD-', '');
      // find that group in state
      const parentIndex = this.state.viewConfig.findIndex((item) => item.dataIndex === groupDataIndex);
      if (parentIndex < 0) return;

      const updated = cloneDeep(this.state.viewConfig);
      const children = updated[parentIndex].columns!;
      const [moved] = children.splice(source.index, 1);
      children.splice(destination.index, 0, moved);
      return this.setState({ viewConfig: updated });
    }

    // ROOT (parent) reordering
    const reorder = (list: any, startIndex: number, endIndex: number) => {
      const result = Array.from(list);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);

      return result;
    };

    let newConfig = reorder(this.state.viewConfig, source.index, destination.index) as TenantConfigViewItem[];
    newConfig = orderBy(newConfig, 'pinned', 'asc');
    newConfig = orderBy(
      newConfig.map((x) => {
        if (x.visible) {
          delete x.visible;
          delete x.hidden;
        }
        return x;
      }),
      'visible',
      'desc',
    );

    this.setState({ viewConfig: newConfig });
  };

  showResetConfirmation = () => {
    const modal = document.body.querySelector<HTMLDivElement>('[class^=MuiDialog]');
    modal ? (modal.style.zIndex = '51') : undefined;
    this.setState({
      showResetConfirmation: true,
    });
  };

  hideResetConfirmation = () => {
    this.setState({
      showResetConfirmation: false,
    });
  };

  resetViewConfigurator = () => {
    // TODO:Create better type for updateConfig handler

    const updatedViewConfig = { ...this.props.unmodifiedViewDefn, isDefault: true };
    if (this.props.updateConfig) {
      this.props.updateConfig(updatedViewConfig);
    }
    if (this.props.onSubmit) {
      this.props.onSubmit(updatedViewConfig as TenantConfigViewData);
    }

    this.setState({
      showResetConfirmation: false,
      viewConfig:
        (updatedViewConfig.view as TenantConfigViewItem[]) || (updatedViewConfig.columns as TenantConfigViewItem[]),
      openOverride: false,
    });
  };

  itemVisible = (item: TenantConfigViewItem) => {
    return !isNil(item?.visible) ? item.visible : !isNil(item?.hidden) ? !item.hidden : true;
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchValue: event.currentTarget.value });
  };

  handleClearSearch = () => {
    this.setState({ searchValue: '' });
  };

  getFilteredViewConfig = () => {
    const { viewConfig, searchValue } = this.state;
    if (!searchValue) {
      return viewConfig;
    }

    const searchLower = searchValue.toLowerCase();
    return viewConfig.filter((item) => {
      // Check if the item itself matches
      const itemMatches = (item.viewConfiguratorName || item.text || '').toLowerCase().includes(searchLower);

      // Check if any child columns match
      const childMatches = item.columns?.some((col) =>
        (col.viewConfiguratorName || col.text || '').toLowerCase().includes(searchLower),
      );

      return itemMatches || childMatches;
    });
  };

  render() {
    const { showPinCheckboxForGrid } = this.props;
    const { showResetConfirmation, openOverride, searchValue } = this.state;
    const filteredViewConfig = this.getFilteredViewConfig();
    return (
      <MuiThemeProvider theme={muiTheme}>
        {/* theme provider re-exported here because for reasons that are unclear, the theme doesn't apply witin this component correctly */}
        <TemplateModalWithButton
          buttonComponent={
            // TODO: INT-2114: Fix this properly
            <SubheaderQuickActionButton
              iconClass={'fas fa-wrench'}
              style={{ opacity: 1, position: 'relative' }}
              tooltipText="Configure metrics in this view"
              buttonDataQa="subheader-wrench-button"
            />
          }
          title={`Configure ${this.props.subheaderTitle} Metrics`}
          onOpen={this.onOpen}
          onSubmit={this.onSubmit}
          onCancel={this.onCancel}
          showSubmit={true}
          openOverride={openOverride}
          styleOverrides={{ maxWidth: '550px' }} // define a max width only in this location, so title text wraps
          leftAction={
            <button data-qa="wrench-reset-btn" className={classes(resetButtonStyles, 'primary')} onClick={this.showResetConfirmation}>
              RESET
            </button>
          }
        >
          <ConfirmationModal
            isOpen={showResetConfirmation}
            descriptionText="Are you sure you want to reset?"
            onConfirm={() => this.resetViewConfigurator()}
            onCancel={() => this.hideResetConfirmation()}
          />
          <div className={viewConfiguratorStyle} data-qa="wrench-modal-container">
            <div className="columns-title-container">
              <p>Metric</p>
              {showPinCheckboxForGrid && <p>Pin</p>}
              <p>Show</p>
            </div>
            <div className="search-container">
              <OutlinedInput
                aria-label="Search columns"
                className="wrench-search"
                fullWidth={true}
                inputProps={{
                  'data-qa': 'ViewConfiguratorSearchInput',
                }}
                endAdornment={
                  <IconButton size={'small'} onClick={this.handleClearSearch}>
                    <Close />
                  </IconButton>
                }
                value={searchValue}
                onChange={this.handleSearchChange}
                placeholder="Search columns..."
              />
            </div>
            <div className="columns-grid">
              {filteredViewConfig && (
                <DragDropContext onDragEnd={this.onDragEnd}>
                  <Droppable droppableId="root" type="ROOT">
                    {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
                    {(provided: any, snapshot: any) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={getListStyle(snapshot.isDraggingOver, filteredViewConfig.length)}
                      >
                        {filteredViewConfig.map((item: TenantConfigViewItem, index: number) => {
                          // find the real index in the un-filtered array, needed for edits during search
                          const realIndex = this.state.viewConfig.findIndex((i) => i.dataIndex === item.dataIndex);
                          if (item.hideFromConfigurator) {
                            return;
                          }
                          const id = (item.dataIndex || item.text) + index;
                          const hasChildren = !isNil(item.columns);
                          return (
                            <Draggable key={id} draggableId={id} index={index}>
                              {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
                              {(provided: any, snapshot: any) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                  data-qa="menu-option"
                                >
                                  <StyledAccordion square={true}>
                                    <StyledAccordionSummary
                                      expandIcon={hasChildren ? <ExpandMore /> : null}
                                      style={{
                                        backgroundColor: snapshot.isDragging ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.05)',
                                        cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                      }}
                                    >
                                      <StyledTypography variant="body2" noWrap={true} hasIcon={hasChildren}>
                                        <Tooltip title={item.text}>
                                          <span data-qa="view-config-metric-title">
                                            {item.viewConfiguratorName || item.text}
                                          </span>
                                        </Tooltip>
                                      </StyledTypography>

                                      <div className="checkbox-container">
                                        {showPinCheckboxForGrid && (
                                          <Checkbox
                                            title={'Pinned?'}
                                            checked={this.isPinned(item.pinned as string)}
                                            onClick={() => this.onClickPinned(item)}
                                          />
                                        )}
                                        <Checkbox
                                          title={'Visible?'}
                                          checked={this.itemVisible(item)}
                                          onClick={(e) => {
                                            // prevent the checkbox click from also toggling the accordion
                                            e.stopPropagation();
                                            this.onClickVisible(item);
                                          }}
                                        />
                                      </div>
                                    </StyledAccordionSummary>
                                    <StyledAccordionDetails>
                                      {hasChildren && (
                                        <Droppable
                                          droppableId={`child-group-${item.dataIndex}`}
                                          type={`CHILD-${item.dataIndex}`}
                                        >
                                          {(childProv: any) => {
                                            return (
                                              <div ref={childProv.innerRef} {...childProv.droppableProps}>
                                                {item.columns?.map((col, idx) => (
                                                  <Draggable
                                                    key={`child-${item.dataIndex}-${col.dataIndex}`}
                                                    draggableId={`child-${item.dataIndex}-${col.dataIndex}`}
                                                    index={idx}
                                                  >
                                                    {(colProv, colSnapshot) => (
                                                      <div
                                                        ref={colProv.innerRef}
                                                        {...colProv.draggableProps}
                                                        {...colProv.dragHandleProps}
                                                        style={{
                                                          ...getItemStyle(
                                                            colSnapshot.isDragging,
                                                            colProv.draggableProps.style,
                                                          ),
                                                          display: 'grid',
                                                          marginLeft: 0,
                                                          marginRight: 0,
                                                          padding: '4px 0px 4px 8px',
                                                        }}
                                                        className="child-metric-container"
                                                      >
                                                        <StyledTypography hasIcon={hasChildren} noWrap variant="body2">
                                                          <Tooltip title={col.text}>
                                                            <span>{col.viewConfiguratorName || col.text}</span>
                                                          </Tooltip>
                                                        </StyledTypography>
                                                        <Checkbox
                                                          checked={this.itemVisible(col)}
                                                          onClick={() => this.onChildrenClickVisible(col, idx, index)}
                                                        />
                                                      </div>
                                                    )}
                                                  </Draggable>
                                                ))}
                                                {childProv.placeholder}
                                              </div>
                                            );
                                          }}
                                        </Droppable>
                                      )}
                                    </StyledAccordionDetails>
                                  </StyledAccordion>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>

          </div>
        </TemplateModalWithButton>
      </MuiThemeProvider>
    );
  }
}
